import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { APIRoutes, AppMethods, AppRoutes, ErrorStatus, SourceRoutes } from '@core/enums';
import { ShowErrorService } from '@core/services';
import { catchError, EMPTY, throwError } from 'rxjs';
import { Router } from '@angular/router';

/**
 * HTTP interceptor for global error handling of API requests.
 * Handles specific error codes, retries authorization, and shows user-friendly messages.
 */
export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  // Inject all required services
  const showErrorService = inject(ShowErrorService);
  const router = inject(Router);

  const errorRequest = request;
  const method = request.method;

  // Handle the HTTP response and catch errors
  return next(errorRequest).pipe(
    catchError(error => {
      const status = error.status;
      const url = error.url;
      // List of error codes considered unexpected
      const unexpectedErrorsCodes = [
        ErrorStatus.BadRequest,
        ErrorStatus.Forbidden,
        ErrorStatus.NotFound,
        ErrorStatus.TechnicalError,
        ErrorStatus.Conflict,
        ErrorStatus.Locked,
        ErrorStatus.NotAllowed,
        ErrorStatus.Unauthorized,
        ErrorStatus.UnprocessableEntity,
      ];

      // URLs for which errors should be skipped (e.g., static files, i18n)
      const skipUrlFiles = [`${APIRoutes.API}/${SourceRoutes.FILES}`];
      const skipUrls = [SourceRoutes.I18N, SourceRoutes.IMAGES, SourceRoutes.CONTENT, ...skipUrlFiles];

      // Skip error handling for certain URLs (e.g., static files, i18n)
      if (skipUrls.findIndex(a => url.includes(a)) != -1) {
        if (url.includes(SourceRoutes.IMAGES) && method !== AppMethods.GET) {
          return throwError(() => error);
        }

        if (
          skipUrlFiles.findIndex(s => s.indexOf(url)) != -1 &&
          (method === AppMethods.POST || method === AppMethods.PUT)
        ) {
          return throwError(() => error);
        }

        return EMPTY;
      }

      if (status === ErrorStatus.TokenExpired) {
        router.navigate([AppRoutes.RENEWAL]);
        return EMPTY;
      }

      showErrorService.show(unexpectedErrorsCodes.includes(status) ? status : null, null);
      // For all other errors, propagate the error
      return throwError(() => error);
    }),
  );
};
